import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';

import { db } from '@/config';

/**
 * Rozliczenia AI organizacji w tokenach HotelSpot (kolekcja `hotels/{id}/aiBilling`).
 *
 * Trzy odczyty: plan (pula i marża), dokument bieżącego miesiąca (sumy i rozbicia) oraz
 * dokumenty dni tego miesiąca (wykres i „dzisiaj"). Zastępuje `useGetAiUsageLive`, który
 * czytał WSZYSTKIE dni z przestarzałej kolekcji `aiUsage` oraz budżety USD z `aiTokenLimit`.
 * Miesiąc i dzień liczone według czasu polskiego, tak jak zapisuje backend.
 */

export type AiSource =
  | 'guestChat' | 'guestVoice' | 'formAssistant' | 'translation' | 'panelHelp' | 'contentGeneration'
  | 'documentScore' | 'kbCompression' | 'surveyAnalysis' | 'taskTranslation' | 'agentAvatar' | 'adCreative';

export interface AiUsageBucket {
  tokens: number;
  costUsd: number;
  clientUsd: number;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  /** Wygenerowane obrazy (np. okładki); dokumenty sprzed licznika nie mają pola, wtedy 0. */
  images: number;
}

export interface AiDayUsage extends AiUsageBucket {
  day: string;
}

/** Aktywny przydział dodatkowych tokenów (ważny 12 miesięcy od przydziału). */
export interface AiExtraTokenGrant {
  id: string;
  tokens: number;
  remaining: number;
  grantedAt: number;
  expiresAt: number;
  source: 'admin' | 'purchase';
}

export interface AiBillingData {
  period: string;
  /** `null` = bez limitu; 0 = AI wyłączone. */
  monthlyTokens: number | null;
  hasPlan: boolean;
  tokensUsed: number;
  /** Pula miesięczna + dodatkowe tokeny. `null` = bez limitu. */
  remainingTokens: number | null;
  /** Sama pula miesięczna. `null` = bez limitu. */
  monthlyRemainingTokens: number | null;
  extraRemainingTokens: number;
  /** Aktywne przydziały dodatkowych tokenów, od najwcześniej tracącego ważność. */
  extraGrants: AiExtraTokenGrant[];
  exhausted: boolean;
  requestCount: number;
  /** Obrazy wygenerowane w okresie (pole `images` okresu, 0 dla starszych dokumentów). */
  images: number;
  clientUsd: number;
  nextRenewalAt: number;
  today: AiUsageBucket;
  days: AiDayUsage[];
  bySource: Partial<Record<AiSource, AiUsageBucket>>;
  byModel: Record<string, AiUsageBucket>;
  isEmpty: boolean;
}

const BILLING_TIME_ZONE = 'Europe/Warsaw';
const dayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: BILLING_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
});

export const billingDayKey = (date = new Date()): string => dayFormat.format(date);
export const billingPeriodId = (date = new Date()): string => billingDayKey(date).slice(0, 7);

/** Początek następnego miesiąca czasu polskiego jako czas lokalny użytkownika (do wyświetlenia). */
export const nextRenewalAt = (date = new Date()): number => {
  const [year, month] = billingPeriodId(date).split('-').map(Number);
  const guess = Date.UTC(month === 12 ? year + 1 : year, month === 12 ? 0 : month, 1);
  // Europe/Warsaw to UTC+1 lub UTC+2: lokalna północ wypada 1 lub 2 godziny wcześniej w UTC.
  const candidates = [2, 1, 0].map((h) => guess - h * 3_600_000);
  return candidates.find((ms) => billingDayKey(new Date(ms)).endsWith('-01') && !billingDayKey(new Date(ms - 60_000)).endsWith('-01')) ?? guess;
};

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const emptyBucket = (): AiUsageBucket => ({
  tokens: 0, costUsd: 0, clientUsd: 0, requestCount: 0, inputTokens: 0, outputTokens: 0, images: 0,
});

export const toBucket = (raw: unknown): AiUsageBucket => {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    tokens: num(r.tokens),
    costUsd: num(r.costUsd),
    clientUsd: num(r.clientUsd),
    requestCount: num(r.requestCount),
    inputTokens: num(r.inputTokens),
    outputTokens: num(r.outputTokens),
    images: num(r.images),
  };
};

/**
 * Źródła poza pulą hotelu (backend `UNBILLED_SOURCES`, liczone w `unbilled`, nie w `tokensUsed`).
 * Tłumaczenia treści (`translation`) od audytu #188 schodzą z puli, więc są w rozbiciu: suma
 * `bySource` musi się zgadzać z `tokensUsed`. `taskTranslation` to tylko historyczne wpisy legacy.
 */
const HIDDEN_SOURCES: readonly string[] = ['taskTranslation'];

export const withoutHiddenSources = <K extends string>(map: Record<K, AiUsageBucket>): Record<K, AiUsageBucket> => {
  const visible = Object.entries(map).filter(([key]) => !HIDDEN_SOURCES.includes(key));
  return Object.fromEntries(visible) as Record<K, AiUsageBucket>;
};

const toBucketMap = <K extends string>(raw: unknown): Record<K, AiUsageBucket> => {
  const out = {} as Record<K, AiUsageBucket>;
  if (raw && typeof raw === 'object') {
    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
      out[key as K] = toBucket(value);
    });
  }
  return out;
};

/**
 * Aktywne przydziały dodatkowych tokenów: niewycofane, niewygasłe, z resztą. Lustro
 * `isGrantActive` z backendu (functions-shared `aiBilling/extraTokens`).
 */
const activeExtraGrants = (raw: unknown, now: number): AiExtraTokenGrant[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((g: Record<string, unknown>) => ({
      id: String(g.id ?? ''),
      tokens: num(g.tokens),
      remaining: Math.max(0, num(g.tokens) - num(g.used)),
      grantedAt: num(g.grantedAt),
      expiresAt: num(g.expiresAt),
      source: (g.source === 'purchase' ? 'purchase' : 'admin') as AiExtraTokenGrant['source'],
      revoked: !!g.revokedAt,
    }))
    .filter((g) => !g.revoked && g.expiresAt > now && g.remaining > 0)
    .map(({ revoked: _revoked, ...g }) => g)
    .sort((a, b) => a.expiresAt - b.expiresAt);
};

export const AI_BILLING_QUERY_KEY = 'ai-billing';

export const useGetAiBilling = (
  organizationId: string | undefined,
): UseQueryResult<AiBillingData, FirebaseError> => useQuery<AiBillingData, FirebaseError>({
  queryKey: [AI_BILLING_QUERY_KEY, organizationId],
  enabled: !!organizationId,
  staleTime: 1000 * 60 * 5,
  refetchInterval: 1000 * 60 * 5,
  queryFn: async (): Promise<AiBillingData> => {
    const hotelId = organizationId!;
    const period = billingPeriodId();
    const periodRef = doc(db, 'hotels', hotelId, 'aiBilling', period);
    const [planSnap, periodSnap, daysSnap] = await Promise.all([
      getDoc(doc(db, 'hotels', hotelId, 'aiBilling', 'plan')),
      getDoc(periodRef),
      getDocs(collection(periodRef, 'days')),
    ]);

    const plan = planSnap.exists() ? planSnap.data() : null;
    const rawMonthly = plan?.monthlyTokens;
    const monthlyTokens = typeof rawMonthly === 'number' && Number.isFinite(rawMonthly) && rawMonthly >= 0 ? Math.floor(rawMonthly) : null;
    const periodData = periodSnap.exists() ? periodSnap.data() : {};
    const tokensUsed = num(periodData.tokensUsed);
    // Najpierw pula miesięczna, potem dodatkowe tokeny. Zużycie ponad pulę, którego backend
    // jeszcze nie pobrał z przydziałów, odejmujemy, żeby nie pokazać zbyt dużego zapasu.
    const extraGrants = activeExtraGrants(plan?.extraGrants, Date.now());
    const pendingOverflow = monthlyTokens === null
      ? 0
      : Math.max(0, tokensUsed - monthlyTokens - num(periodData.extraTokensCharged));
    const extraRemainingTokens = Math.max(0, extraGrants.reduce((sum, g) => sum + g.remaining, 0) - pendingOverflow);
    const monthlyRemainingTokens = monthlyTokens === null ? null : Math.max(0, monthlyTokens - tokensUsed);
    let remainingTokens: number | null = null;
    if (monthlyTokens === 0) remainingTokens = 0;
    else if (monthlyRemainingTokens !== null) remainingTokens = monthlyRemainingTokens + extraRemainingTokens;

    const todayKey = billingDayKey();
    const days: AiDayUsage[] = daysSnap.docs
      .map((d) => {
        const dayData = d.data();
        // Dokument dnia ma sumę tokenów w `tokensUsed` (jak okres), nie w `tokens` jak mapy źródeł.
        return { day: d.id, ...toBucket(dayData), tokens: num(dayData.tokensUsed ?? dayData.tokens) };
      })
      .sort((a, b) => a.day.localeCompare(b.day));

    return {
      period,
      monthlyTokens,
      // Dokument planu z samymi przydziałami dodatkowymi nie przypisuje puli.
      hasPlan: !!plan && Object.prototype.hasOwnProperty.call(plan, 'monthlyTokens'),
      tokensUsed,
      remainingTokens,
      monthlyRemainingTokens,
      extraRemainingTokens,
      extraGrants,
      exhausted: remainingTokens !== null && remainingTokens <= 0,
      requestCount: num(periodData.requestCount),
      images: num(periodData.images),
      clientUsd: num(periodData.clientUsd),
      nextRenewalAt: nextRenewalAt(),
      today: days.find((d) => d.day === todayKey) ?? emptyBucket(),
      days,
      bySource: withoutHiddenSources(toBucketMap<AiSource>(periodData.bySource)),
      byModel: toBucketMap<string>(periodData.byModel),
      isEmpty: !plan && !periodSnap.exists(),
    };
  },
});
